import type { ReactNode, SVGProps } from 'react';
import { cx } from '../../../utils/cx';
import styles from './Icon.module.scss';

const paths = {
  home: <path d="M4 12 12 5l8 7v7a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1z" />,
  doc: (
    <>
      <path d="M5 4.5h9l5 5V20H5z" />
      <path d="M14 4.5v5h5M8 13h8M8 16.5h5" />
    </>
  ),
  chat: (
    <>
      <path d="M5 5h14v11H9l-4 3z" />
      <path d="M9 9h6M9 12h4" />
    </>
  ),
  suppliers: (
    <>
      <path d="M4 19h16M6 19V9l6-4 6 4v10" />
      <path d="M9 12h6M9 15h4" />
    </>
  ),
  admin: (
    <>
      <path d="M4 20h16V8H4zM8 8V4h8v4" />
      <path d="M9 13h6M9 16h4" />
    </>
  ),
  headset: (
    <>
      <path d="M5 12a7 7 0 0 1 14 0v5h-4v-5h4M5 12v5h4v-5z" />
      <path d="M15 19h-3" />
    </>
  ),
  news: (
    <>
      <path d="M5 6h14v13H5z" />
      <path d="M8 3v6M16 3v6M8 12h8M8 15.5h5" />
    </>
  ),
  faq: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M10 9.5a2.2 2.2 0 1 1 3.1 2c-.8.5-1.1.9-1.1 2M12 16.5h.01" />
    </>
  ),
  chevronDown: <path d="m7 10 5 5 5-5" />,
  chevronRight: <path d="M9 6l6 6-6 6" />,
  chevronLeft: <path d="M15 6l-6 6 6 6" />,
  bell: <path d="M6 16h12l-1-2V9a5 5 0 0 0-10 0v5zM10 19h4" />,
  database: (
    <>
      <ellipse cx="12" cy="5.5" rx="7" ry="3" />
      <path d="M5 5.5v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6M5 11.5v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
    </>
  ),
  gear: (
    <>
      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
      <path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.4 1A8 8 0 0 0 15 6l-.3-2.6h-4L10.4 6A8 8 0 0 0 8.8 7L6.4 6 4.5 9.5 6.5 11a7 7 0 0 0 0 2l-2 1.5L6.4 18l2.4-1A8 8 0 0 0 10.4 18l.3 2.6h4L15 18a8 8 0 0 0 1.6-1l2.4 1 2-3.5-2-1.5a7 7 0 0 0 0-1z" />
    </>
  ),
  logout: <path d="M10 5H5v14h5M14 8l4 4-4 4M18 12H9" />,
  upload: (
    <>
      <path d="M12 16V4M8 8l4-4 4 4" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </>
  ),
  paperclip: (
    <path d="M15 8.5 9.6 14a2.1 2.1 0 0 0 3 3l5.4-5.5a4.2 4.2 0 0 0-6-6l-5.4 5.5a6.3 6.3 0 0 0 9 9l4.4-4.5" />
  ),
  send: (
    <>
      <path d="M4.5 12 20 4.5 15.5 20l-3.6-6.2z" />
      <path d="m11.9 13.8 8.1-9.3" />
    </>
  ),
  check: <path d="m5 12.5 4.5 4.5L19 7" />,
  alert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  bulb: (
    <>
      <path d="M9 18h6M10 21h4" />
      <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5.9 1.2.9 1.9V16h5.2v-.2c0-.7.3-1.4.9-1.9A6 6 0 0 0 12 3z" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  file: (
    <>
      <path d="M6 3.5h7l5 5V20.5H6z" />
      <path d="M13 3.5v5h5" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof paths;

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Размер стороны в px. Иконки сервиса живут в диапазоне 10–26. */
  size?: number;
  strokeWidth?: number;
}

/** Единый набор штриховых иконок. Цвет наследуется от родителя через currentColor. */
export function Icon({ name, size = 14, strokeWidth = 1.8, className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      className={cx(styles.icon, className)}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}
